import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import qs from "qs";
import { setLoading } from "../redux/slices/ModalSlice";
import { removeUser } from "../redux/slices/UserSlice";
import store from "../redux/store";
import sharedConstants from "../shared/constants";
import { alertError, getToken } from "../shared/utils";
import { HttpError, HttpResponse, Instance } from "./types";

export const axiosUtils = {
  GET: "get",
  POST: "post",
  PUT: "put",
  DELETE: "delete",
  PATCH: "patch",
  AXIOS_CONFIG_DEFAULT: {
    timeout: 30000,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
    paramsSerializer: (params: unknown) =>
      qs.stringify(params, { arrayFormat: "brackets" }),
    withCredentials: false,
  },
};

export const createAxiosInstance = (axiosConfig: AxiosRequestConfig) => {
  const instance = axios.create(axiosConfig);
  instance.interceptors.request.use(
    (config) => {
      config.headers = {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
        Expires: "0",
        "content-type": "application/json",
        "Access-Control-Allow-Origin": "*",
      };
      if (config.params)
        Object.keys(config.params).forEach((key) => {
          const queryValue = config.params[key];

          if (typeof queryValue === "string" && queryValue === "") {
            config.params[key] = undefined;
          }
        });

      store.dispatch(setLoading());
      if (getToken("tirepick_repairshop_access_token") !== null)
        config.headers = {
          ...config.headers,
          authorization: getToken("tirepick_repairshop_access_token") || "",
        };
      return config;
    },
    (err) => {
      Promise.reject(err);
      store.dispatch(setLoading());
    }
  );
  instance.interceptors.response.use(
    (response) => {
      store.dispatch(setLoading());

      return response;
    },
    (err) => {
      const error = new HttpError(err);
      store.dispatch(setLoading());

      if (error.status === undefined)
        error.message = sharedConstants.message.ALERT_MESSAGE.FAILED_REQUEST;
      if (error.status < 500)
        if (error.message === "" || error.message === undefined) {
          error.message = sharedConstants.message.ALERT_MESSAGE.FAILED_REQUEST;
        }

      if (error.message?.includes("토큰") || error.message?.includes("세션")) {
        alertError("토큰이 유효하지 않아 로그아웃됩니다.");
        store.dispatch(removeUser());

        window.setTimeout(() => {
          window.location.reload();
        }, 2100);
        return Promise.reject();
      }

      return Promise.reject(error);
    }
  );
  return instance;
};

export const request = async (
  instance: AxiosInstance,
  method: string,
  url: string,
  queryParams: unknown,
  requestBody: unknown
): Promise<HttpResponse> => {
  switch (method) {
    case axiosUtils.GET:
      return await instance.get(url, { params: queryParams });
    case axiosUtils.POST:
      return await instance.post(url, requestBody, { params: queryParams });
    case axiosUtils.PUT:
      return await instance.put(url, requestBody, { params: queryParams });
    case axiosUtils.PATCH:
      return await instance.patch(url, requestBody, { params: queryParams });
    case axiosUtils.DELETE:
      return await instance.delete(url, {
        params: queryParams,
        data: requestBody,
      });
    default:
      return null;
  }
};

export class AppAxiosInstance implements Instance {
  private instance: any;

  private unauthorizedInstance: any;

  constructor(private readonly baseURL = process.env.REACT_APP_ROOT_URL) {}

  public readonly createInstance = (): AxiosInstance => {
    const { baseURL } = this;
    const axiosConfig = {
      ...axiosUtils.AXIOS_CONFIG_DEFAULT,
      baseURL:
        process.env.REACT_APP_MODE === "PROD"
          ? baseURL
          : window.localStorage.getItem("url") || baseURL,
    };
    return createAxiosInstance(axiosConfig);
  };

  public readonly getInstance = (unauthorized = false): AxiosInstance => {
    if (!unauthorized) {
      if (!this.instance) {
        this.instance = this.createInstance();
      }
      return this.instance;
    }
    if (!this.unauthorizedInstance) {
      this.unauthorizedInstance = this.createInstance();
    }
    return this.unauthorizedInstance;
  };
}

export const requestInstance = new AppAxiosInstance();
